<template>
  <div>
    <b-modal
      ref="unitModal"
      size="lg"
      hide-header
      hide-footer
      header-class="unit-modal-header"
      @hidden="clearData"
      body-class="p-0"
      class="unit-modal-wrapper"
    >
      <div class="unit-container d-flex">
        <!-- Mobile-only header - only visible on mobile -->
        <div
          class="unit-header w-100 px-4 pt-2 pb-2 d-flex d-md-none justify-content-between align-items-center"
        >
          <h5 class="mb-0 unit-name">
            {{ record.unit_name || "New Unit" }}
          </h5>
          <div class="unit-actions d-flex">
            <span
              v-if="!isEditing && !isNewRecord"
              class="modal-btn action-btn"
              @click="deleteRecord"
            >
              <i
                class="delete-unit fi-trash nav-icon"
                v-b-tooltip.hover.top="'Delete Unit'"
              ></i>
            </span>
            <span class="modal-btn action-btn" @click="toggleEditMode">
              <i
                v-if="isEditing"
                class="save-unit fi-save nav-icon"
                v-b-tooltip.hover.top="'Save Unit'"
              ></i>
              <i
                v-else
                class="edit-unit fi-pencil nav-icon"
                v-b-tooltip.hover.top="'Edit Unit'"
              ></i>
            </span>
            <span class="modal-btn action-btn" @click="hide">
              <i
                class="cancel-contact fi-x nav-icon"
                v-b-tooltip.hover.top="'Close Window'"
              ></i>
            </span>
          </div>
        </div>

        <!-- Side Navigation - only visible on desktop -->
        <div class="unit-sidenav d-none d-md-flex">
          <div class="sidenav-header mb-4">
            <div class="nav-section-title text-center">
              <div class="unit-avatar mx-auto mb-2">
                <img
                  v-if="record.unit_image"
                  :src="record.unit_image"
                  class="avatar-img"
                  alt="Unit photo"
                />
                <div
                  v-else
                  class="avatar-placeholder d-flex align-items-center justify-content-center"
                >
                  {{ getInitials }}
                </div>
              </div>
            </div>
          </div>

          <div class="unit-nav-items">
            <div
              v-for="(section, index) in sections"
              :key="index"
              class="unit-nav-item"
              :class="{ active: activeSection === section.id }"
              @click="scrollToSection(section.id)"
            >
              <i :class="section.icon"></i>
              <span class="ml-2">{{ section.title }}</span>
            </div>
          </div>

          <!-- Hidden file input for photo upload -->
          <input
            style="display: none"
            ref="fileInput"
            type="file"
            @change="onFileChanged"
          />
        </div>

        <!-- Main Content Area -->
        <div class="unit-content" ref="unitContent" @scroll="handleScroll">
          <!-- Desktop header - only visible on desktop -->
          <div
            class="desktop-header px-4 pt-2 pb-2 d-none d-md-flex justify-content-between align-items-center"
          >
            <h5 class="mb-0 unit-name">
              {{ record.unit_name || "New Unit" }}
            </h5>
            <div class="unit-actions d-flex">
              <span
                v-if="!isEditing && !isNewRecord"
                class="modal-btn action-btn"
                @click="deleteRecord"
              >
                <i
                  class="delete-unit fi-trash nav-icon"
                  v-b-tooltip.hover.top="'Delete Unit'"
                ></i>
              </span>
              <span class="modal-btn action-btn" @click="toggleEditMode">
                <i
                  v-if="isEditing"
                  class="save-unit fi-save nav-icon"
                  v-b-tooltip.hover.top="'Save Unit'"
                ></i>
                <i
                  v-else
                  class="edit-unit fi-pencil nav-icon"
                  v-b-tooltip.hover.top="'Edit Unit'"
                ></i>
              </span>
              <span class="modal-btn action-btn" @click="hide">
                <i
                  class="cancel-contact fi-x nav-icon"
                  v-b-tooltip.hover.top="'Close Window'"
                ></i>
              </span>
            </div>
          </div>

          <!-- Main content area with scrollable sections -->
          <div class="unit-body px-4">
            <!-- Unit Information Section -->
            <b-card
              no-body
              class="mb-3 section-card"
              id="details"
              ref="details"
            >
              <b-card-header
                header-tag="header"
                class="p-2 border-bottom-0 text-left"
                role="tab"
              >
                <i class="fi-home mr-2"></i>
                {{ $t("unit_modal.details_title") || "Details" }}
              </b-card-header>
              <b-card-body>
                <div v-if="!isEditing" class="readonly-display">
                  <div class="readonly-row">
                    <label class="readonly-label">{{ $t("unit_name") }}</label>
                    <span class="readonly-value">{{
                      record.unit_name || "Not set"
                    }}</span>
                  </div>
                  <div class="readonly-row">
                    <label class="readonly-label">{{ $t("unit_rate") }}</label>
                    <span class="readonly-value">{{
                      record.unit_rate || "Not set"
                    }}</span>
                  </div>
                  <div class="readonly-row">
                    <label class="readonly-label">{{
                      $t("unit_description")
                    }}</label>
                    <span class="readonly-value">{{
                      record.unit_description || "Not set"
                    }}</span>
                  </div>
                  <div class="readonly-row">
                    <label class="readonly-label">{{ $t("occ_adults") }}</label>
                    <span class="readonly-value">{{
                      record.occ_adults || "Not set"
                    }}</span>
                  </div>
                  <div class="readonly-row">
                    <label class="readonly-label">{{
                      $t("occ_children")
                    }}</label>
                    <span class="readonly-value">{{
                      record.occ_children || "Not set"
                    }}</span>
                  </div>
                  <div class="readonly-row">
                    <label class="readonly-label">{{
                      $t("default_occupancy")
                    }}</label>
                    <span class="readonly-value">{{
                      record.default_occupancy || "Not set"
                    }}</span>
                  </div>
                  <!-- temp commented out. needs to be looped in with subscription handlers -->
                  <!-- <div class="readonly-row">
                    <label class="readonly-label">{{
                      $t("unit_type.show_in_db")
                    }}</label>
                    <span class="readonly-value">
                      <i
                        :class="
                          record.show_in_db
                            ? 'fi-check text-success'
                            : 'fi-x text-danger'
                        "
                        class="mr-1"
                      ></i>
                      {{ record.show_in_db ? $t("yes") : $t("no") }}
                    </span>
                  </div> -->
                  <div v-if="virtualKey.usesVirtualKey" class="readonly-row">
                    <label class="readonly-label">{{
                      $t("virtual key name")
                    }}</label>
                    <span class="readonly-value">{{
                      record.virtual_key_unit_name || "Not set"
                    }}</span>
                  </div>
                </div>
                <b-form v-else>
                  <b-row>
                    <b-col sm="12" md="6">
                      <b-form-group
                        :label="$t('unit_name')"
                        label-for="UnitNameInput"
                      >
                        <b-form-input
                          v-model="record.unit_name"
                          id="UnitNameInput"
                          type="text"
                          :state="unitNameValid"
                          :placeholder="$t('unit_name')"
                          @change="$v.record.unit_name.$touch"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="6">
                      <b-form-group
                        :label="$t('unit_rate')"
                        label-for="UnitRateInput"
                      >
                        <b-form-input
                          v-model="record.unit_rate"
                          id="UnitRateInput"
                          type="number"
                          :state="unitRateValid"
                          :placeholder="$t('unit_rate')"
                          @change="$v.record.unit_rate.$touch"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('unit_description')"
                        label-for="UnitDescriptionInput"
                      >
                        <b-form-textarea
                          v-model="record.unit_description"
                          id="UnitDescriptionInput"
                          :state="unitDescriptionValid"
                          :placeholder="$t('unit_description')"
                          rows="3"
                          @change="$v.record.unit_description.$touch"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="4">
                      <b-form-group
                        :label="$t('occ_adults')"
                        label-for="OccAdultsInput"
                      >
                        <b-form-input
                          v-model="record.occ_adults"
                          id="OccAdultsInput"
                          type="number"
                          :state="occAdultsValid"
                          :placeholder="$t('occ_adults')"
                          @change="$v.record.occ_adults.$touch"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="4">
                      <b-form-group
                        :label="$t('occ_children')"
                        label-for="OccChildrenInput"
                      >
                        <b-form-input
                          v-model="record.occ_children"
                          id="OccChildrenInput"
                          type="number"
                          :placeholder="$t('occ_children')"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="4">
                      <b-form-group
                        :label="$t('default_occupancy')"
                        label-for="DefaultOccupancyInput"
                      >
                        <b-form-input
                          v-model="record.default_occupancy"
                          id="DefaultOccupancyInput"
                          type="number"
                          :state="defaultOccupancyValid"
                          :placeholder="$t('default_occupancy')"
                          @change="$v.record.default_occupancy.$touch"
                        />
                      </b-form-group>
                    </b-col>
                    <!-- <b-col sm="12" md="6">
                      <b-form-group
                        :label="$t('unit_type.show_in_db')"
                        label-for="ShowInDbToggle"
                      >
                        <toggle-button
                          class="active-toggle"
                          :value="record.show_in_db"
                          :sync="true"
                          :labels="{ checked: $t('yes'), unchecked: $t('no') }"
                          :width="100"
                          :height="33"
                          style="font-size: 16px"
                          color="#82C7EB"
                          @change="
                            record.show_in_db = $event.value ? true : false
                          "
                        />
                      </b-form-group>
                    </b-col> -->
                    <b-col v-if="virtualKey.usesVirtualKey" sm="12" md="6">
                      <b-form-group
                        :label="$t('virtual key name')"
                        label-for="VirtualKeyInput"
                      >
                        <div class="d-flex align-items-center">
                          <b-form-input
                            v-if="virtualKey.isEditingVK"
                            v-model="record.virtual_key_unit_name"
                            id="VirtualKeyInput"
                            type="text"
                            :placeholder="$t('virtual key unit name')"
                            class="mr-2"
                          />
                          <span v-else class="mr-2">
                            {{ record.virtual_key_unit_name || "Not set" }}
                          </span>
                          <b-button
                            size="sm"
                            variant="outline-primary"
                            @click="editVKName"
                          >
                            {{ !virtualKey.isEditingVK ? "Setup" : "Link" }}
                          </b-button>
                        </div>
                        <div
                          v-if="virtualKey.didError"
                          class="text-danger mt-1"
                        >
                          {{ virtualKey.error }}
                        </div>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-form>
              </b-card-body>
            </b-card>

            <!-- Photos Section -->
            <b-card no-body class="mb-3 section-card" id="photos" ref="photos">
              <b-card-header
                header-tag="header"
                class="p-2 border-bottom-0 text-left"
                role="tab"
              >
                <i class="fi-camera mr-2"></i>
                {{ $t("unit_modal.photos_title") || "Photos" }}
              </b-card-header>
              <b-card-body>
                <div class="photo-upload-section">
                  <b-button
                    v-if="isEditing"
                    variant="outline-secondary"
                    @click="$refs['fileInput'].click()"
                    class="mb-3"
                  >
                    <i class="fi-camera mr-2"></i>
                    Upload Photo
                  </b-button>

                  <div v-if="record.unit_image" class="current-photo">
                    <img
                      :src="record.unit_image"
                      alt="Unit photo"
                      class="unit-photo"
                    />
                  </div>
                  <div v-else class="no-photo text-center py-3">
                    <p class="text-muted mb-0">
                      <i class="fi-camera mr-2"></i> No photo uploaded
                    </p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Tags Section -->
            <b-card
              v-if="record.id"
              no-body
              class="section-card"
              id="tags"
              ref="tags"
            >
              <b-card-header
                header-tag="header"
                class="p-2 border-bottom-0 text-left"
                role="tab"
              >
                <i class="fi-price-tag mr-2"></i>
                {{ $t("unit_modal.tags_title") || "Tags" }}
              </b-card-header>
              <b-card-body>
                <div
                  v-if="!isEditing && selectedTags.length > 0"
                  class="tags-display"
                >
                  <div
                    v-for="tag in selectedTags"
                    :key="tag.id"
                    class="tag-card"
                    :class="{ 'tag-card-readonly': !isEditing }"
                  >
                    <div class="tag-content">
                      <i class="fi-tag tag-icon"></i>
                      <span class="tag-name">{{ tag.name }}</span>
                    </div>
                    <button
                      v-if="isEditing"
                      class="tag-remove-btn"
                      @click="onRemoveTag(tag)"
                      v-b-tooltip.hover.top="'Remove tag'"
                    >
                      <i class="fi-x"></i>
                    </button>
                  </div>
                </div>
                <div v-else-if="!isEditing" class="text-center py-3">
                  <p class="text-muted mb-0">
                    <i class="fi-tag mr-2"></i> No tags assigned
                  </p>
                </div>
                <div v-if="isEditing">
                  <Taggregator
                    ref="tagsTaggregator"
                    label="Unit Tags"
                    labelKey="name"
                    name="tags"
                    :items="tags"
                    :selections.sync="selectedTags"
                    :minimum="0"
                    :quickAddCallback="onQuickAddTag"
                    :quickAddMinWordCount="1"
                    :quickAddMinWordLength="1"
                    :quickAddDescription="'Please provide valid item name'"
                    :quickAddHasUniqueName="true"
                    :onAdd="onAddTag"
                    :onRemove="onRemoveTag"
                  >
                    <template slot="result" slot-scope="{ result }">
                      <span class="result-label">
                        {{ result.name }}
                      </span>
                    </template>
                    <template slot="selection" slot-scope="{ selection }">
                      {{ selection.name }}
                    </template>
                  </Taggregator>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>
    </b-modal>
    <image-upload-modal ref="upload-modal" />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import axios from "axios";
import API from "@/api";
import AlertMixin from "@/mixins/alert-mixin";

import { required, minLength } from "vuelidate/lib/validators";

export default {
  name: "UnitModal",

  components: {
    ImageUploadModal: () => import("@/components/modals/ImageUploadModal"),
    Taggregator: () => import("@/components/Taggregator"),
  },

  mixins: [AlertMixin],

  data() {
    return {
      record: {
        unit_name: null,
        unit_rate: null,
        unit_description: null,
        occ_adults: 2,
        occ_children: 0,
        default_occupancy: 2,
        virtual_key_unit_name: null,
        unit_image: null,
        unit_image_id: null,
        unit_type_id: null,
        channex_room_id: null,
      },
      selectedFile: null,
      unit_id: 0,
      isNewRecord: false,
      isEditing: false,
      is_used: false,
      activeSection: "details",
      sections: [
        {
          id: "details",
          title: "Unit Details",
          icon: "fi-home",
        },
        {
          id: "photos",
          title: "Photos",
          icon: "fi-camera",
        },
        {
          id: "tags",
          title: "Tags",
          icon: "fi-price-tag",
        },
      ],
      virtualKey: {
        isLoading: false,
        usesVirtualKey: false,
        isEditingVK: false,
        didError: false,
        error: "",
      },
      selectedTags: [],
      loading: false,
    };
  },

  computed: {
    ...mapState({
      tags: (state) => state.tags.tags,
    }),

    getInitials() {
      const name = this.record.unit_name || "UN";
      const words = name.split(" ");
      if (words.length >= 2) {
        return `${words[0].charAt(0).toUpperCase()}${words[1]
          .charAt(0)
          .toUpperCase()}`;
      }
      return name.substring(0, 2).toUpperCase();
    },

    unitNameValid: function () {
      return (
        !this.$v.record.unit_name.$dirty || !this.$v.record.unit_name.$invalid
      );
    },

    unitRateValid: function () {
      return (
        !this.$v.record.unit_rate.$dirty || !this.$v.record.unit_rate.$invalid
      );
    },

    unitDescriptionValid: function () {
      return (
        !this.$v.record.unit_description.$dirty ||
        !this.$v.record.unit_description.$invalid
      );
    },

    occAdultsValid: function () {
      return (
        !this.$v.record.occ_adults.$dirty || !this.$v.record.occ_adults.$invalid
      );
    },

    defaultOccupancyValid: function () {
      return (
        !this.$v.record.default_occupancy.$dirty ||
        !this.$v.record.default_occupancy.$invalid
      );
    },

    alertText: function () {
      return "Are you sure you want to delete " + this.record.unit_name + "?";
    },
  },

  mounted() {
    this.virtualKey.usesVirtualKey =
      this.$store.getters.getVendor.uses_virtual_key;
  },

  methods: {
    ...mapActions(["fetchTags"]),

    edit(_unitTypeId, _record) {
      this.init();

      if (_record) {
        this.isEditing = false;
        this.isNewRecord = false;
        this.record.id = _record.id;
        this.record.unit_rate = _record.unit_rate;
        this.record.occ_adults = _record.occ_adults;
        this.record.occ_children = _record.occ_children;
        this.record.default_occupancy = _record.default_occupancy;
        this.record.unit_name = _record.unit_name;
        this.record.unit_description = _record.unit_description;
        this.record.virtual_key_unit_name = _record.virtual_key_unit_name;
        this.record.show_in_db = !!_record.show_in_db;
        this.record.unit_type_id = _unitTypeId;
        this.record.channex_room_id = _record.channex_room_id;
        this.is_used = _record.is_used;

        this.getUnitImage(_record.id);
        this.getUnitTags(_record.id);
        this.fetchTags();
      } else {
        this.record.unit_type_id = _unitTypeId;
        this.isEditing = true;
        this.isNewRecord = true;
      }

      this.show();
    },

    show() {
      if (this.$refs.unitModal) {
        this.selectedTags = [];
        this.activeSection = "details";
        this.$refs.unitModal.show();
      }
    },

    hide() {
      if (this.$refs.unitModal) {
        this.$refs.unitModal.hide();
      }
    },

    clearData() {
      this.record = {};
      this.selectedTags = [];
      this.activeSection = "details";
    },

    scrollToSection(sectionId) {
      this.activeSection = sectionId;
      const element = this.$refs[sectionId];
      if (element) {
        this.$refs.unitContent.scrollTop = element.offsetTop - 80;
      }
    },

    handleScroll() {
      // Determine which section is most visible in the viewport
      const scrollTop = this.$refs.unitContent.scrollTop;
      const sections = ["details", "photos", "tags"];
      const offset = 80; // Match the scroll offset used in scrollToSection

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = this.$refs[sections[i]];
        if (section) {
          const sectionTop = section.offsetTop - offset;
          const sectionBottom = sectionTop + section.offsetHeight;

          if (scrollTop >= sectionTop && scrollTop < sectionBottom) {
            this.activeSection = sections[i];
            break;
          }
        }
      }
    },

    init() {
      this.record = {
        unit_name: null,
        unit_rate: null,
        occ_adults: 2,
        occ_children: 0,
        default_occupancy: 2,
        unit_description: null,
        virtual_key_unit_name: null,
        unit_image: null,
        unit_image_id: null,
        unit_type_id: null,
        channex_room_id: null,
      };
    },

    onFileChanged(event) {
      this.selectedFile = event.target.files[0];

      if (!this.selectedFile) {
        return;
      }

      if (this.selectedFile.size / 2048 >= 1000) {
        // over 2M
        this.errorNotification("Maximum image size of 2MB");
        return;
      }

      // Create a preview URL for the image
      this.record.unit_image = URL.createObjectURL(this.selectedFile);

      // If this is a new unit, we'll upload after unit creation
      if (!this.isNewRecord) {
        this.uploadUnitImage();
      }
    },

    uploadUnitImage() {
      if (!this.selectedFile) {
        return;
      }

      let formData = new FormData();
      let uploadUri = `${API.BASE_URL}/unitimages`;

      if (this.record.unit_image_id) {
        uploadUri = `${uploadUri}/${this.record.unit_image_id}`;
      }

      formData.append("image", this.selectedFile, this.selectedFile.name);
      formData.append("unit_id", this.record.id);
      formData.append("is_logo", 1);

      this.$Progress.start();
      if (this.$refs["upload-modal"]) {
        this.$refs["upload-modal"].show();
      }

      axios
        .post(uploadUri, formData, {
          ...API.RequestHeaders(),
          "Content-Type": "multipart/form-data",
        })
        .then((res) => {
          this.record.unit_image = res.data.md_uri;
          this.record.unit_image_id = res.data.id;
          this.selectedFile = null;
        })
        .catch((err) => {
          this.errorNotification(err || "Failed to upload unit image");
        })
        .finally(() => {
          this.$Progress.finish();
          if (this.$refs["upload-modal"]) {
            this.$refs["upload-modal"].hide();
          }
        });
    },

    handleAddRecord() {
      this.$store
        .dispatch("addUnit", this.record)
        .then((response) => {
          // If we have an image to upload, do it now that we have a unit ID
          if (this.selectedFile) {
            this.record.id = response.id;
            return this.uploadUnitImage();
          }
        })
        .then(() => {
          this.hide();
          this.infoNotification(
            `${this.record.unit_name} successfully created.`
          );
        })
        .catch((_msg) => {
          this.errorNotification(_msg);
          this.isEditing = true;
        });
    },

    toggleEditMode() {
      if (this.isEditing) {
        if (this.$v.$invalid) {
          this.errorNotification("Check input fields");
          return;
        }
        if (this.isNewRecord) {
          this.handleAddRecord();
        } else {
          this.handleEditRecord();
        }

        this.isEditing = false;
      } else {
        this.isEditing = true;
      }
    },

    handleEditRecord() {
      this.$store
        .dispatch("updateUnit", this.record)
        .then(() => {
          this.infoNotification(
            `${this.record.unit_name} successfully updated.`
          );
        })
        .catch((_msg) => {
          this.errorNotification(_msg);
          this.isEditing = true;
        });
    },

    deleteRecord() {
      if (this.is_used) {
        this.errorNotification(
          "This unit has been used for the bookings, so it can not be deleted"
        );
        return;
      }

      this.showConfirm("Are you sure?").then((result) => {
        if (result.value) {
          this.confirmDelete();
        }
      });
    },

    confirmDelete() {
      this.$store
        .dispatch("removeUnit", this.record.id)
        .then(() => {
          this.infoNotification("Room successfully deleted.");
          this.$store.dispatch("fetchAccommodations");
        })
        .catch((_msg) => {
          this.errorNotification(_msg);
          this.isEditing = true;
        });
      this.hide();
    },

    editVKName() {
      if (!this.virtualKey.isEditingVK) {
        this.virtualKey.isEditingVK = true;
      } else {
        this.virtualKey.isLoading = true;

        let postObj = {
          vk_name: this.record.virtual_key_unit_name,
          unit_id: this.record.id,
        };

        axios
          .post(
            `${API.BASE_URL}${API.VIRTUAL_KEY_UNIT_LINK}`,
            postObj,
            API.RequestHeaders()
          )
          .then((response) => {
            this.virtualKey.isLoading = false;
            if (response.status === 200) {
              this.virtualKey.didError = false;
              this.virtualKey.isConnected = true;
              this.virtualKey.isEditingVK = false;
            } else {
              this.virtualKey.didError = true;
              this.virtualKey.error = "Incorrect Unit Name";
            }
          })
          .catch((error) => {
            this.virtualKey.isLoading = false;
            this.virtualKey.didError = true;
            this.virtualKey.error = "Incorrect Unit Name";
          });
      }
    },

    getUnitImage(unit_id) {
      let requestUri = `${API.BASE_URL}/unitimages/${unit_id}`;

      axios
        .get(requestUri, API.RequestHeaders())
        .then((res) => {
          res.data.map((image) => {
            if (image.is_logo) {
              this.record.unit_image = image.md_uri;
              this.record.unit_image_id = image.id;
            }
          });
        })
        .catch((err) => {
          this.errorNotification(err || "Failed to fetch unit images");
        });
    },

    getUnitTags(unit_id) {
      let requestUri = `${API.BASE_URL}/unittags?unit_id=${unit_id}`;

      axios
        .get(requestUri, API.RequestHeaders())
        .then((res) => {
          this.selectedTags = res.data.map((unit_tag) => ({
            id: unit_tag.tag_id,
            name: unit_tag.tag.name,
            record_id: unit_tag.id,
          }));
        })
        .catch((err) => {
          this.errorNotification(err || "Failed to fetch unit tags");
        });
    },

    onQuickAddTag(name, items) {
      this.$store
        .dispatch("createTag", {
          name: name,
        })
        .then((response) => {
          items.push(response);
          this.onAddTag(response);
          this.successNotification("Tag succesfully created!");
        })
        .catch((err) => {
          this.errorNotification("Failed to create tag! :(");
        });
    },

    onAddTag(tag) {
      let requestUri = `${API.BASE_URL}/unittags`;
      let params = {
        unit_id: this.record.id,
        tag_id: tag.id,
      };

      axios
        .post(requestUri, params, API.RequestHeaders())
        .then((res) => {
          let tag_ = res.data.original;
          let id = this.selectedTags.findIndex((tag) => tag.id === tag_.tag_id);

          if (id !== -1) {
            this.selectedTags[id].record_id = tag_.id;
          }

          this.successNotification(
            `Tag '${tag.name}' has been added to the unit`
          );
        })
        .catch((err) => {
          this.errorNotification(err || "Failed to fetch unit image");
        });
    },

    onRemoveTag(tag) {
      let requestUri = `${API.BASE_URL}/unittags/${tag.record_id}`;

      axios
        .delete(requestUri, API.RequestHeaders())
        .then((res) => {
          this.successNotification(
            `Tag '${tag.name}' has been removed from the unit`
          );
        })
        .catch((err) => {
          this.errorNotification(err || "Failed to delete unit tag");
        });
    },
  },

  validations: {
    record: {
      unit_name: {
        required,
        minLength: minLength(1),
      },
      unit_rate: {
        required,
      },
      occ_adults: {
        required,
      },
      default_occupancy: {
        required,
      },
      unit_description: {
        required,
        minLength: minLength(1),
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.unit-modal-wrapper {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

:deep(.modal-dialog) {
  @media (min-width: 775px) and (max-width: 991.98px) {
    width: 90vw !important;
    max-width: 800px !important;
    margin: 1.75rem auto !important;
  }
}

.unit-container {
  max-height: 80vh;
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  flex-direction: column;
  background-color: #f5f7fa;

  @media (min-width: 768px) {
    flex-direction: row;
  }
}

.unit-header {
  position: relative;
  background: white;
  z-index: 10;
  padding-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  order: 1;
}

.unit-sidenav {
  width: 100%;
  background-color: #eef1f6;
  border-right: none;
  border-bottom: 1px solid #dee2e6;
  padding: 1rem;
  flex-direction: column;
  height: 80vh;
  border-radius: 8px 0 0 8px;
  order: 1;
  width: 200px;
  border-right: 1px solid #dee2e6;
}

.unit-nav-items {
  display: flex;
  flex-direction: column;
  overflow-x: visible;
  padding-bottom: 0;
}

.unit-nav-item {
  padding: 0.6rem 0.75rem;
  margin: 0 0 0.5rem 0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 123, 255, 0.1);
  }

  &.active {
    background-color: rgba(0, 123, 255, 0.15);
    border-left: 3px solid #007bff;
    font-weight: 500;
    color: #0056b3;
  }
}

.nav-section-title {
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unit-content {
  flex: 1;
  overflow-y: auto;
  height: 75vh;
  position: relative;
  order: 3;
  background-color: #fff;

  @media (min-width: 768px) {
    height: 80vh;
    border-radius: 0 8px 8px 0;
  }

  @media (max-width: 767.98px) {
    height: 80vh;
    border-radius: 0 0 8px 8px;
  }
}

.unit-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  margin-bottom: 0.25rem;
  opacity: 0.9;
  box-shadow: 0 2px 6px rgba(0, 123, 255, 0.2);

  &:hover {
    opacity: 0.9;
  }
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  color: #1976d2;
  font-size: 2.2rem;
  font-weight: 500;
}

.unit-body {
  padding-bottom: 2rem;
}

.section-card {
  scroll-margin-top: 70px;
  margin-top: 10px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:deep(.card-header) {
    background-color: #f8f9fa;
    border-bottom-color: #e9ecef;
  }

  &:deep(.card-body) {
    background-color: #ffffff;
    padding: 1.25rem;
  }

  &:deep(.form-control) {
    background-color: #fcfcfc;
    border: 1px solid #ced4da;

    &:focus {
      background-color: #ffffff;
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }
}

.badge-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: left;

  .badge {
    font-size: 14px;
    line-height: 20px;
    padding: 3px 10px;
  }
}

.photo-upload-section {
  .current-photo {
    display: flex;
    justify-content: center;

    .unit-photo {
      max-width: 300px;
      max-height: 200px;
      border-radius: 6px;
      border: 1px solid #e9ecef;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }

  .no-photo {
    background-color: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 6px;
  }
}

.modal-btn {
  cursor: pointer;
  display: block;
  font-size: 14px;
  padding: 7px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &.action-btn {
    margin: 0 5px;
    padding: 8px 12px;
    font-size: 16px;

    i.nav-icon {
      font-size: 22px;
    }
  }

  i.nav-icon {
    color: #007bff;

    &:hover {
      color: #0056b3;
    }

    &.cancel-contact {
      color: #dc3545;

      &:hover {
        color: #bd2130;
      }
    }

    &.delete-unit {
      color: #dc3545;

      &:hover {
        color: #bd2130;
      }
    }
  }
}

.sidenav-header {
  padding: 1.5rem 0.5rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.sidenav-icon {
  font-size: 2.5rem;
  color: #007bff;
  display: block;
  margin: 0 auto;
}

.unit-name {
  font-weight: 500;
  color: #333;
  max-width: 70%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desktop-header {
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.tags-display {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tag-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &.tag-card-readonly {
    background-color: #f8f9fa;
    border-color: #e9ecef;

    &:hover {
      background-color: #f1f3f4;
      border-color: #e9ecef;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    }
  }
}

.tag-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.tag-icon {
  color: #007bff;
  margin-right: 0.5rem;
  font-size: 1rem;
}

.tag-name {
  font-weight: 500;
  color: #495057;
  font-size: 0.9rem;
}

.tag-remove-btn {
  background: none;
  border: none;
  color: #6c757d;
  padding: 0.25rem;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #dc3545;
    color: white;
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
  }

  i {
    font-size: 0.8rem;
  }
}

.readonly-display {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.readonly-row {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f3f4;

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
}

.readonly-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  min-width: 140px;

  @media (min-width: 768px) {
    margin-bottom: 0;
    margin-right: 1rem;
  }
}

.readonly-value {
  color: #495057;
  font-size: 0.95rem;
  line-height: 1.4;
  word-break: break-word;

  &:empty::before {
    content: "Not set";
    color: #adb5bd;
    font-style: italic;
  }
}
</style>

<style scoped>
i.nav-icon {
  font-size: 1.8rem;
  padding: 3px;
}
</style>
